import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { File as FileIcon, FilePdf, Image as ImageIcon, Download, ArrowClockwise, Warning } from "@phosphor-icons/react";
import AttachmentPreviewModal from "./AttachmentPreviewModal";

/**
 * Renders a single chat attachment.
 *  • Audio  → inline <audio controls>
 *  • Image  → thumbnail (click = OPEN PREVIEW, never auto-download)
 *  • PDF    → file chip (click = OPEN PDF PREVIEW)
 *  • Other  → file chip (click = OPEN INFO MODAL with Download button)
 *
 * The chip renders a *separate* Download icon on non-preview file chips
 * so power users can still one-click download without opening the modal.
 * Clicking the chip body opens preview; clicking the Download icon on
 * the chip downloads directly. `event.stopPropagation()` on the Download
 * icon prevents the click from also bubbling into the preview trigger.
 *
 * On fetch failure surfaces an error state with a retry button — critical
 * for prod debuggability. The previous version silently console.warn'd,
 * which is why users saw a broken-image icon with no recourse.
 */
export default function AttachmentChip({ att, msg, mine }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [preview, setPreview] = useState(false);
  const objectUrlRef = useRef(null);

  const isImage = att.content_type?.startsWith("image/") || att.kind === "image";
  const isPdf   = att.content_type === "application/pdf"   || att.kind === "pdf";
  const isAudio = att.content_type?.startsWith("audio/")   || att.kind === "audio";
  const isVideo = att.content_type?.startsWith("video/")   || att.kind === "video";

  // Fetch the blob when a preview-capable type. Non-preview types (docs,
  // spreadsheets) don't need this and only load on download click.
  useEffect(() => {
    if (!isImage && !isAudio && !isVideo) return;
    if (!att.path) { setError("Attachment is missing its file id"); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/team-chat/files/${encodeURIComponent(att.path)}`, { responseType: "blob" })
      .then(r => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        objectUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(err => {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg = status ? `HTTP ${status}` : (err?.message || "unknown");
        setError(msg);
        // eslint-disable-next-line no-console
        console.warn("[AttachmentChip] fetch failed:", att.path, msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [att.path, isImage, isAudio, isVideo, retryTick]);

  // Explicit download — used by the error banner retry-download button
  // and by the chip's small Download icon on non-preview file rows. NOT
  // called by the chip's own body click (that opens the preview modal).
  const download = async (e) => {
    e?.stopPropagation();
    try {
      const r = await api.get(`/team-chat/files/${encodeURIComponent(att.path)}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename || "file";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e2) {
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      alert(`Download failed${status ? ` (HTTP ${status})` : ""}${detail ? `: ${detail}` : ""}`);
    }
  };

  const openPreview = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setPreview(true);
  };

  // ── Error banner shared across preview types.
  if (error && (isImage || isAudio || isVideo)) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 min-w-[240px] bg-[color-mix(in_srgb,_var(--danger)_10%,_transparent)] border border-[var(--danger)] text-[12px]"
        data-testid={`att-error-${att.path}`}
      >
        <Warning size={16} className="text-[var(--danger)] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium">Attachment failed to load</div>
          <div className="text-[11px] text-[var(--text-muted)] truncate">{error} — {att.original_filename || "file"}</div>
        </div>
        <button
          onClick={() => setRetryTick(t => t + 1)}
          className="flex items-center gap-1 px-2 py-1 text-[11px] border border-[var(--border)] hover:bg-white/50"
          data-testid={`att-retry-${att.path}`}
        >
          <ArrowClockwise size={12} /> Retry
        </button>
        <button
          onClick={download}
          className="flex items-center gap-1 px-2 py-1 text-[11px] border border-[var(--border)] hover:bg-white/50"
          data-testid={`att-error-download-${att.path}`}
        >
          <Download size={12} /> Download
        </button>
      </div>
    );
  }

  if (isAudio) {
    const mm = att.duration_sec ? String(Math.floor(att.duration_sec / 60)).padStart(2, "0") : null;
    const ss = att.duration_sec ? String(att.duration_sec % 60).padStart(2, "0") : null;
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 min-w-[260px] bg-white/60 border border-[var(--border)] rounded" data-testid={`att-audio-${att.path}`}>
        {blobUrl ? (
          <audio controls src={blobUrl} className="flex-1 h-8" style={{ maxWidth: 260 }}>
            Your browser does not support audio.
          </audio>
        ) : (
          <div className="flex-1 text-[12px] text-[var(--text-muted)]">{loading ? "Loading voice note…" : "Preparing…"}</div>
        )}
        {mm && <span className="font-mono text-[11px] text-[var(--text-muted)]">{mm}:{ss}</span>}
      </div>
    );
  }

  if (isVideo) {
    return (
      <>
        <button type="button" onClick={openPreview} className="max-w-[320px] block" data-testid={`att-video-${att.path}`}
                title="Open preview">
          {blobUrl
            ? <video src={blobUrl} muted className="w-full max-h-[240px] object-cover border border-[var(--border)] pointer-events-none" />
            : <div className="w-[280px] h-[160px] flex items-center justify-center bg-[var(--bg-sidebar)] border border-[var(--border)] text-[12px] text-[var(--text-muted)]">
                {loading ? "Loading video…" : "Preparing…"}
              </div>}
        </button>
        {preview && <AttachmentPreviewModal att={att} msg={msg} initialBlobUrl={blobUrl} onClose={() => setPreview(false)} />}
      </>
    );
  }

  if (isImage) {
    return (
      <>
        <button type="button" onClick={openPreview} className="block" data-testid={`att-image-${att.path}`}
                title="Open preview">
          {blobUrl
            ? <img src={blobUrl} alt={att.original_filename} className="max-w-[260px] max-h-[200px] object-cover border border-[var(--border)]" />
            : <div className={`w-[200px] h-[120px] flex items-center justify-center ${mine ? "bg-white/10" : "bg-[var(--bg-sidebar)]"} border border-[var(--border)]`}>
                {loading ? <span className="text-[11px] text-[var(--text-muted)]">Loading…</span> : <ImageIcon size={28} className="opacity-50" />}
              </div>}
        </button>
        {preview && <AttachmentPreviewModal att={att} msg={msg} initialBlobUrl={blobUrl} onClose={() => setPreview(false)} />}
      </>
    );
  }
  return (
    <>
      <div data-testid={`att-file-${att.path}`}
           className={`flex items-center gap-2 px-2 py-1.5 text-[12px] ${mine ? "bg-white/15 hover:bg-white/25" : "bg-[var(--bg-sidebar)] hover:bg-[var(--border)]"} border ${mine ? "border-white/30" : "border-[var(--border)]"}`}>
        <button type="button" onClick={openPreview}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
                title="Open preview"
                data-testid={`att-file-open-${att.path}`}>
          {isPdf ? <FilePdf size={18} /> : <FileIcon size={18} />}
          <span className="flex-1 truncate max-w-[180px]">{att.original_filename || "file"}</span>
          <span className={`text-[10px] ${mine ? "text-white/70" : "text-[var(--text-muted)]"}`}>
            {att.size ? `${(att.size / 1024).toFixed(0)}kb` : ""}
          </span>
        </button>
        <button type="button" onClick={download}
                className={`p-1 rounded ${mine ? "hover:bg-white/25" : "hover:bg-[var(--border)]"}`}
                title="Download"
                data-testid={`att-file-download-${att.path}`}>
          <Download size={14} />
        </button>
      </div>
      {preview && <AttachmentPreviewModal att={att} msg={msg} initialBlobUrl={null} onClose={() => setPreview(false)} />}
    </>
  );
}
